import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { History, RefreshCw, Search, FileDown } from "lucide-react";

const ACTION_LABELS = {
  "user.create": "User created",
  "user.update": "User updated",
  "user.approve": "User approved",
  "user.suspend": "User suspended",
  "user.unsuspend": "User unsuspended",
  "user.reset_password": "Password reset",
  "user.delete": "User deleted",
  "team.create": "Team created",
  "team.update": "Team updated",
  "team.delete": "Team deleted",
  "team.branding_update": "Team branding updated",
  "match.update": "Match updated",
  "match.finalize": "Match finalized",
  "match.approve": "Match approved",
  "auction.bid": "Auction bid",
  "auction.sold": "Auction sold",
  "config.update": "Master config updated",
  "site_content.update": "Site content updated",
  "player.registration_verify": "Registration verified",
  "player.registration_refund": "Registration refunded",
};

function actionColor(action) {
  if (action.startsWith("user.delete") || action.startsWith("team.delete")) return "#EF4444";
  if (action.includes("suspend")) return "#F59E0B";
  if (action.includes("approve") || action.includes("create")) return "#00A86B";
  if (action.includes("config") || action.includes("site_content")) return "#7DD3FC";
  if (action.startsWith("auction")) return "#F4B400";
  if (action.startsWith("match")) return "#E32424";
  return "#9CA3AF";
}

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: "all", entity_type: "all", q: "" });
  const [actions, setActions] = useState([]);

  const fetchPage = async ({ before_id = null, append = false } = {}) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filter.action !== "all") params.action = filter.action;
      if (filter.entity_type !== "all") params.entity_type = filter.entity_type;
      if (before_id) params.before_id = before_id;
      const { data } = await api.get("/audit-logs", { params });
      setItems((prev) => append ? [...prev, ...data.items] : data.items);
      setHasMore(!!data.has_more);
      setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  // Initial load: fetch actions + first page
  useEffect(() => {
    (async () => {
      try {
        const [acts, page] = await Promise.all([
          api.get("/audit-logs/actions"),
          api.get("/audit-logs", { params: { limit: 50 } }),
        ]);
        setActions(acts.data);
        setItems(page.data.items);
        setHasMore(!!page.data.has_more);
        setTotal(page.data.total);
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  // Refetch when action/entity filter changes (skip initial render via empty-actions guard)
  useEffect(() => {
    if (actions.length === 0) return;
    (async () => {
      const params = { limit: 50 };
      if (filter.action !== "all") params.action = filter.action;
      if (filter.entity_type !== "all") params.entity_type = filter.entity_type;
      try {
        const { data } = await api.get("/audit-logs", { params });
        setItems(data.items);
        setHasMore(!!data.has_more);
        setTotal(data.total);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [actions.length, filter.action, filter.entity_type]);

  const loadMore = () => {
    if (items.length === 0 || !hasMore) return;
    fetchPage({ before_id: items[items.length - 1].id, append: true });
  };

  const refresh = () => fetchPage();

  const filtered = useMemo(() => {
    const q = (filter.q || "").toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const blob = `${i.action} ${i.actor_email || ""} ${i.entity_type || ""} ${i.entity_id || ""} ${JSON.stringify(i.details || {})}`.toLowerCase();
      return blob.includes(q);
    });
  }, [items, filter.q]);

  const entityTypes = useMemo(() => {
    const set = new Set(items.map((i) => i.entity_type).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const downloadCSV = () => {
    const header = ["created_at", "action", "actor_email", "actor_role", "entity_type", "entity_id", "details"];
    const rows = filtered.map((i) => [
      i.created_at, i.action, i.actor_email || "", i.actor_role || "", i.entity_type || "", i.entity_id || "",
      JSON.stringify(i.details || {}).replace(/"/g, '""'),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit_logs_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"><History className="w-3.5 h-3.5" /> Activity Trail</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white" data-testid="audit-page-title">Audit Logs</h1>
          <div className="text-xs text-zinc-400 mt-1">{total} total · {filtered.length} shown{hasMore && " (more available)"}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} variant="outline" size="sm" className="border-white/10 text-zinc-300 hover:bg-white/5" data-testid="audit-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
          <Button onClick={downloadCSV} variant="outline" size="sm" className="border-white/10 text-zinc-300 hover:bg-white/5" data-testid="audit-csv"><FileDown className="w-3.5 h-3.5 mr-1.5" /> CSV</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="audit-filters">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Search action / actor / details…" className="bg-[#11241b] border-white/10 text-white pl-9 w-72" data-testid="audit-search" />
        </div>
        <Select value={filter.action} onValueChange={(v) => setFilter({ ...filter, action: v })}>
          <SelectTrigger className="w-56 bg-[#11241b] border-white/10 text-white" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent className="bg-[#11241b] border-white/10 text-white">
            <SelectItem value="all">All actions</SelectItem>
            {actions.map((a) => <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.entity_type} onValueChange={(v) => setFilter({ ...filter, entity_type: v })}>
          <SelectTrigger className="w-48 bg-[#11241b] border-white/10 text-white" data-testid="audit-entity-filter"><SelectValue placeholder="All entities" /></SelectTrigger>
          <SelectContent className="bg-[#11241b] border-white/10 text-white">
            <SelectItem value="all">All entities</SelectItem>
            {entityTypes.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-[#11241b] border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="px-4 py-3 w-44">When</th>
                <th className="px-4 py-3 w-48">Action</th>
                <th className="px-4 py-3 w-56">Actor</th>
                <th className="px-4 py-3 w-36">Entity</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500" data-testid="audit-empty">No audit entries match this filter.</td></tr>}
              {!loading && filtered.map((i) => {
                const color = actionColor(i.action);
                return (
                  <tr key={i.id} className="border-b border-white/5 hover:bg-white/[0.02] align-top" data-testid={`audit-row-${i.id}`}>
                    <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">{new Date(i.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold" style={{ background: `${color}22`, color, border: `1px solid ${color}55` }} data-testid={`audit-action-${i.id}`}>
                        {ACTION_LABELS[i.action] || i.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white text-sm">{i.actor_email || "—"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 capitalize">{(i.actor_role || "").replace("_", " ")}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="text-zinc-300">{i.entity_type || "—"}</div>
                      <div className="text-zinc-600 font-mono truncate max-w-[120px]" title={i.entity_id}>{i.entity_id || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {i.diff && Object.keys(i.diff).length > 0 ? (
                        <div className="max-w-md space-y-1" data-testid={`audit-diff-${i.id}`}>
                          {Object.entries(i.diff).slice(0, 6).map(([key, chg]) => (
                            <div key={key} className="flex flex-col text-[11px]">
                              <span className="text-[#F4B400] font-mono">{key}</span>
                              <span className="pl-2 text-red-300/80 line-through truncate">− {JSON.stringify(chg.before)}</span>
                              <span className="pl-2 text-emerald-300 truncate">+ {JSON.stringify(chg.after)}</span>
                            </div>
                          ))}
                          {Object.keys(i.diff).length > 6 && (
                            <div className="text-[10px] text-zinc-500 pl-2">…and {Object.keys(i.diff).length - 6} more field changes</div>
                          )}
                        </div>
                      ) : i.details && Object.keys(i.details).length > 0 ? (
                        <pre className="whitespace-pre-wrap font-mono text-[11px] text-zinc-300 max-w-md">{JSON.stringify(i.details, null, 0)}</pre>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {hasMore && (
        <div className="flex justify-center mt-4">
          <Button onClick={loadMore} disabled={loading} variant="outline" className="border-white/10 text-zinc-300 hover:bg-white/5" data-testid="audit-load-more">
            {loading ? "Loading…" : `Load 50 more · ${total - items.length} remaining`}
          </Button>
        </div>
      )}
    </div>
  );
}
